import React, { useEffect, useState } from 'react';
import { outlineApi } from '../../api/outlines';
import { storyBibleApi } from '../../api/storyBible';
import { DATED_NOTE_LINE_COPY as COPY } from '../../copy/datedNoteLineCopy';

export function sequenceChoices(outline) {
  const root = outline?.data?.sections || outline?.sections || [];
  const choices = [];
  const walk = (nodes, sequenceName) => {
    (nodes || []).forEach((node) => {
      if (!node || node.id == null) return;
      const type = node.section_type;
      const name = (node.display_name || '').trim() || COPY.unnamed;
      if (type === 'sequence') {
        choices.push({ id: String(node.id), label: name });
        walk(node.sub_sections, name);
      } else if (type === 'scene') {
        choices.push({
          id: String(node.id),
          label: sequenceName ? `${sequenceName} — ${name}` : name,
        });
      } else {
        walk(node.sub_sections, sequenceName);
      }
    });
  };
  walk(root, '');
  return choices;
}

function labelFor(choices, sectionId) {
  const found = choices.find((choice) => choice.id === String(sectionId));
  return found ? found.label : '';
}

export default function DatedNoteLines({ storyId, noteId }) {
  const [lines, setLines] = useState([]);
  const [choices, setChoices] = useState([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [text, setText] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [lineBody, outlineBody] = await Promise.all([
          storyBibleApi.listDatedLines(storyId, noteId),
          outlineApi.getOutline(storyId).catch((err) => {
            if (err?.status === 404) return null;
            throw err;
          }),
        ]);
        if (cancelled) return;
        const payload = lineBody?.data || lineBody || {};
        setAvailable(payload.available !== false);
        setLines(Array.isArray(payload.lines) ? payload.lines : []);
        setChoices(sequenceChoices(outlineBody));
      } catch {
        if (!cancelled) {
          setError(COPY.loadError);
          setLines([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (storyId && noteId) load();
    return () => { cancelled = true; };
  }, [storyId, noteId]);

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setText('');
    setSectionId('');
    setError('');
  };

  const openAdd = () => {
    setEditingId(null);
    setText('');
    setSectionId('');
    setError('');
    setFormOpen(true);
  };

  const openEdit = (line) => {
    setEditingId(line.id);
    setText(line.line_text || '');
    setSectionId(line.section_id ? String(line.section_id) : '');
    setError('');
    setFormOpen(true);
  };

  const save = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setError(COPY.needText);
      return;
    }
    if (!sectionId) {
      setError(choices.length ? COPY.needSequence : COPY.noSequences);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = { line_text: trimmed, section_id: sectionId };
      const saved = editingId
        ? await storyBibleApi.updateDatedLine(storyId, noteId, editingId, body)
        : await storyBibleApi.createDatedLine(storyId, noteId, body);
      const row = saved?.data || saved;
      setLines((current) => {
        if (!editingId) return [...current, row];
        return current.map((line) => (line.id === editingId ? row : line));
      });
      closeForm();
    } catch (err) {
      setError(err?.status === 503 ? COPY.unavailable : COPY.saveError);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (line) => {
    setError('');
    try {
      await storyBibleApi.deleteDatedLine(storyId, noteId, line.id);
      setLines((current) => current.filter((item) => item.id !== line.id));
      if (editingId === line.id) closeForm();
    } catch (err) {
      setError(err?.status === 503 ? COPY.unavailable : COPY.deleteError);
    }
  };

  return (
    <section className="bs-dated-lines" aria-label={COPY.region}>
      <h3 className="bs-edit__label">{COPY.heading}</h3>
      <p className="bs-dated-lines__hint">{COPY.hint}</p>
      {loading && <p className="bs-dated-lines__hint">{COPY.loading}</p>}
      {!loading && !available && <p className="bs-dated-lines__hint">{COPY.unavailable}</p>}
      {!loading && available && lines.length === 0 && !formOpen && (
        <p className="bs-dated-lines__hint">{COPY.empty}</p>
      )}
      {!loading && available && lines.length > 0 && (
        <ul className="bs-dated-lines__list">
          {lines.map((line) => {
            const where = labelFor(choices, line.section_id);
            return (
              <li key={line.id} className="bs-dated-lines__item">
                <p className="bs-dated-lines__where">{where || COPY.missingSection}</p>
                <p className="bs-dated-lines__text">{line.line_text}</p>
                <div className="bs-dated-lines__actions">
                  <button type="button" className="bs-btn bs-btn--secondary" onClick={() => openEdit(line)}>
                    {COPY.edit}
                  </button>
                  <button type="button" className="bs-btn bs-btn--danger" onClick={() => remove(line)}>
                    {COPY.remove}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {!loading && available && formOpen && (
        <div className="bs-dated-lines__form">
          <label className="bs-edit__label" htmlFor="bs-dated-line-text">{COPY.textLabel}</label>
          <textarea
            id="bs-dated-line-text"
            className="bs-edit__textarea"
            rows={3}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          <label className="bs-edit__label" htmlFor="bs-dated-line-section">{COPY.sequenceLabel}</label>
          <select
            id="bs-dated-line-section"
            className="bs-dated-lines__select"
            value={sectionId}
            onChange={(event) => setSectionId(event.target.value)}
          >
            <option value="">{COPY.sequencePlaceholder}</option>
            {choices.map((choice) => (
              <option key={choice.id} value={choice.id}>{choice.label}</option>
            ))}
          </select>
          <div className="bs-dated-lines__actions">
            <button type="button" className="bs-btn bs-btn--primary" onClick={save} disabled={saving}>
              {editingId ? COPY.saveChanges : COPY.save}
            </button>
            <button type="button" className="bs-btn bs-btn--secondary" onClick={closeForm} disabled={saving}>
              {COPY.cancel}
            </button>
          </div>
        </div>
      )}
      {!loading && available && !formOpen && (
        <button type="button" className="bs-btn bs-btn--secondary" onClick={openAdd}>
          {COPY.add}
        </button>
      )}
      {error && <p className="bs-error">{error}</p>}
    </section>
  );
}
